import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/app/StatusBadge";
import { Recycle, ChevronDown, ChevronUp } from "lucide-react";
import { ITAD_IDS } from "@/constants/testIds";
import OneBefore from "@/components/app/OneBefore";
import { useAuth } from "@/context/AuthContext";

const DISPOSITIONS = ["redeploy", "resell", "buyback", "recycle", "destroy", "donate"];

export default function ITAD() {
  const { user } = useAuth?.() || { user: {} };
  const [assets, setAssets] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ asset_id: "", disposition: "recycle", buyback_offer: "" });
  const [showBefore, setShowBefore] = useState(true);

  const load = async () => {
    const [a, i] = await Promise.all([
      api.get("/assets/", { params: { lifecycle_state: "in_warehouse", limit: 100 } }),
      api.get("/itad", { params: { limit: 100 } }),
    ]);
    setAssets(a.data.items || []);
    setRows(i.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.asset_id) return toast.error("Select asset");
    try {
      await api.post("/itad", {
        asset_id: form.asset_id,
        disposition: form.disposition,
        buyback_offer: form.buyback_offer ? parseFloat(form.buyback_offer) : null,
      });
      toast.success("ITAD record created");
      setForm({ asset_id: "", disposition: "recycle", buyback_offer: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const complete = async (id) => {
    try {
      await api.post(`/itad/${id}/complete`, { final_value: null, customer_approved: true });
      toast.success("ITAD completed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Operations</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">IT Asset Disposition</h1>
        <p className="text-sm text-slate-500 mt-0.5">Buyback, recycling, destruction with full chain of custody.</p>
      </div>

      {/* ONE Before · recycle — reuse-first nudge (spec §75, §29-32). */}
      {showBefore && form.disposition === "recycle" && (
        <div data-testid="itad-onebefore">
          <OneBefore
            action="recycle"
            masterCompanyId={user?.master_company_id}
            assetId={form.asset_id || undefined}
            onDismiss={() => setShowBefore(false)}
          />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-5 grid md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Asset</Label>
          <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
            <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Select asset…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Disposition</Label>
          <Select value={form.disposition} onValueChange={(v) => setForm({ ...form, disposition: v })}>
            <SelectTrigger data-testid={ITAD_IDS.dispositionSelect} className="h-10 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DISPOSITIONS.map((d) => <SelectItem key={d} value={d}><span className="capitalize">{d}</span></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Buyback offer (USD)</Label>
          <Input
            type="number"
            className="h-10 mt-1"
            value={form.buyback_offer}
            onChange={(e) => setForm({ ...form, buyback_offer: e.target.value })}
          />
        </div>
        <div className="flex items-end">
          <Button data-testid={ITAD_IDS.createBtn} className="w-full bg-slate-900 hover:bg-slate-800 text-white" onClick={create}>
            <Recycle className="h-4 w-4 mr-1.5" /> Create ITAD
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500">ITAD</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Asset</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Disposition</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Offer</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No ITAD records.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.itad_code}</TableCell>
                <TableCell className="font-mono text-xs">{r.asset_id?.slice(0, 8)}</TableCell>
                <TableCell className="text-sm capitalize">{r.disposition}</TableCell>
                <TableCell className="text-sm">{r.buyback_offer ?? "—"}</TableCell>
                <TableCell><StatusBadge value={r.status} /></TableCell>
                <TableCell>
                  {r.status !== "closed" && (
                    <Button data-testid={ITAD_IDS.completeBtn} size="sm" variant="outline" onClick={() => complete(r.id)}>
                      Complete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
